import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { C, Button, Input, GlassCard } from "@coremonit/design-system";
import { Sparkles, Mail, Lock, ArrowRight } from "lucide-react";
import { useAuth } from "../auth/AuthContext";

export function LoginPage() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("admin@coremonit.io");
  const [password, setPassword] = useState("demo");
  const [loading, setLoading] = useState(false);

  if (user) {
    const dest = (loc.state as { from?: string })?.from ?? "/";
    return <Navigate to={dest} replace />;
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(`Welcome back`);
    } catch (err) {
      toast.error("Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh", background: C.bg, color: C.text,
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20,
      backgroundImage: `radial-gradient(1200px 600px at 20% -10%, ${C.blueDim} 0%, transparent 60%), radial-gradient(900px 500px at 90% 110%, rgba(6,182,212,0.06) 0%, transparent 60%)`,
    }}>
      <GlassCard glow style={{ padding: 32, width: "100%", maxWidth: 420 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: `linear-gradient(135deg, ${C.blue}, ${C.cyan})`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 0 20px rgba(45,127,249,0.4)` }}>
            <Sparkles size={18} color="#fff" />
          </div>
          <div>
            <div style={{ color: C.text, fontSize: 15, fontWeight: 700 }}>CoreMonit</div>
            <div style={{ color: C.textMuted, fontSize: 10, letterSpacing: 0.4, textTransform: "uppercase" }}>Microservice Observability</div>
          </div>
        </div>
        <h1 style={{ color: C.text, fontSize: 20, fontWeight: 700, margin: "0 0 6px" }}>Welcome back</h1>
        <p style={{ color: C.textMuted, fontSize: 12, margin: "0 0 22px" }}>Sign in to your observability workspace.</p>
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label style={{ color: C.textSub, fontSize: 11, fontWeight: 600, display: "block", marginBottom: 6 }}>Email</label>
            <Input value={email} onChange={setEmail} prefix={<Mail size={13} />} placeholder="you@company.io" size="md" />
          </div>
          <div>
            <label style={{ color: C.textSub, fontSize: 11, fontWeight: 600, display: "block", marginBottom: 6 }}>Password</label>
            <Input value={password} onChange={setPassword} prefix={<Lock size={13} />} type="password" size="md" />
          </div>
          <Button type="submit" variant="glow" size="lg" fullWidth loading={loading} iconRight={<ArrowRight size={14} />}>
            Sign in
          </Button>
        </form>
        <div style={{ marginTop: 20, padding: 12, borderRadius: 10, background: C.surfaceHigh, border: `1px solid ${C.border}` }}>
          <div style={{ color: C.textMuted, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 700, marginBottom: 6 }}>Demo credentials</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, color: C.textSub, fontSize: 11 }}>
            <span>admin@coremonit.io</span><span style={{ color: C.text }}>demo</span>
            <span>sre@coremonit.io</span><span style={{ color: C.text }}>demo</span>
            <span>viewer@coremonit.io</span><span style={{ color: C.text }}>demo</span>
          </div>
        </div>
      </GlassCard>
    </div>
  );
}
